import React from "react";
import { Input } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import EmailChipInput from "./EmailChipInput";

/**
 * Email-invite tab: the email chip input plus, once emails are added, a
 * per-user optional-name row list. Presentational — the container owns state and
 * the email/name handlers.
 *
 * @param {object} props
 * @param {{ name: string, email: string }[]} props.selectedEmails
 * @param {string} props.emailSearchValue
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} props.onInputChange
 * @param {(e: React.KeyboardEvent) => void} props.onKeyDown
 * @param {() => void} props.onBlur
 * @param {(email: string) => void} props.onRemove
 * @param {(email: string, name: string) => void} props.onNameChange
 */
export default function EmailInviteTab({
  selectedEmails,
  emailSearchValue,
  onInputChange,
  onKeyDown,
  onBlur,
  onRemove,
  onNameChange,
}) {
  return (
    <>
      <EmailChipInput
        selectedEmails={selectedEmails}
        emailSearchValue={emailSearchValue}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onRemove={onRemove}
      />

      {/* User Details - Show when there are selected emails */}
      {selectedEmails.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <label className="text-[#3f3f46] text-sm font-bold font-['Inter']">
              User Details
            </label>
            <span className="text-xs text-gray-500 font-['Inter']">
              Name is optional
            </span>
          </div>
          <div className="flex flex-col gap-2 max-h-[240px] overflow-y-auto pr-2">
            {selectedEmails.map((user, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors duration-200"
              >
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-blue-600">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-['Inter'] min-w-[60px]">
                      Name:
                    </span>
                    <Input
                      value={user.name}
                      onChange={(e) => onNameChange(user.email, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      placeholder="Enter user name (optional)"
                      className="flex-1"
                      style={{ borderRadius: "6px", fontSize: "13px" }}
                      size="small"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-['Inter'] min-w-[60px]">
                      Email:
                    </span>
                    <span className="text-sm text-gray-700 font-['Inter']">
                      {user.email}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(user.email)}
                  className="text-red-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg transition-all duration-200 shrink-0"
                  title="Remove user"
                >
                  <DeleteOutlined />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
